<template>
  <div ref="root" class="virtual-list" @scroll.passive="onScroll">
    <div class="virtual-list__phantom" :style="{ height: `${totalHeight}px` }" aria-hidden="true" />
    <div class="virtual-list__window" :style="{ transform: `translateY(${offsetY}px)` }">
      <div
        v-for="entry in windowEntries"
        :key="entry.key"
        class="virtual-list__item"
        :style="{ minHeight: `${itemHeight}px` }"
      >
        <slot :item="entry.item" :index="entry.index" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  name: 'VirtualList',
  props: {
    items: { type: Array, default: () => [] },
    itemHeight: { type: Number, default: 52 },
    /** 视口外多渲染几行，减少快速滚动白屏 */
    overscan: { type: Number, default: 4 },
    /** 列表项 key；默认用 index */
    itemKey: {
      type: Function as unknown as () => (item: unknown, index: number) => string | number,
      default: null,
    },
  },
  data() {
    return {
      scrollTop: 0,
      viewportHeight: 320,
      resizeObserver: null as ResizeObserver | null,
    };
  },
  computed: {
    totalHeight(): number {
      return this.items.length * this.itemHeight;
    },
    startIndex(): number {
      const raw = Math.floor(this.scrollTop / this.itemHeight) - this.overscan;
      return Math.max(0, raw);
    },
    endIndex(): number {
      const visibleCount = Math.ceil(this.viewportHeight / this.itemHeight) + this.overscan * 2;
      return Math.min(this.items.length, this.startIndex + visibleCount);
    },
    offsetY(): number {
      return this.startIndex * this.itemHeight;
    },
    windowEntries(): Array<{ item: unknown; index: number; key: string | number }> {
      const list: Array<{ item: unknown; index: number; key: string | number }> = [];
      for (let index = this.startIndex; index < this.endIndex; index += 1) {
        const item = this.items[index];
        const key = this.itemKey ? this.itemKey(item, index) : index;
        list.push({ item, index, key });
      }
      return list;
    },
  },
  watch: {
    items() {
      this.$nextTick(this.measure);
    },
  },
  mounted() {
    this.measure();
    window.addEventListener('resize', this.measure, { passive: true });
    const root = this.$refs.root as HTMLElement | undefined;
    if (root && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(root);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    measure() {
      const root = this.$refs.root as HTMLElement | undefined;
      if (!root) return;
      this.viewportHeight = root.clientHeight || 320;
    },
    onScroll() {
      const root = this.$refs.root as HTMLElement;
      this.scrollTop = root.scrollTop;
    },
    scrollToIndex(index: number) {
      const root = this.$refs.root as HTMLElement | undefined;
      if (!root) return;
      root.scrollTop = Math.max(0, index * this.itemHeight);
      this.scrollTop = root.scrollTop;
    },
  },
});
</script>

<style lang="scss" scoped>
.virtual-list {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.virtual-list__phantom {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  pointer-events: none;
}

.virtual-list__window {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  will-change: transform;
}

.virtual-list__item {
  box-sizing: border-box;
}
</style>
